import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Dialog } from './Dialog';

export interface ConfirmOptions {
  title: string;
  /** What happens, in one or two sentences: the consequence comes first (UX audit #2). */
  consequence: ReactNode;
  /** Names the action ("Deactivate", "Delete"), never "OK". */
  confirmLabel: string;
  /** `danger` for destructive actions (the default), `primary` for the rest. */
  tone?: 'danger' | 'primary';
}

/**
 * One confirmation pattern for actions that cannot be taken back. Render the returned element once
 * and await `confirm(...)`: true when the person chose the named action, false on Cancel, Escape or
 * a click outside.
 */
export function useConfirm(): [ReactNode, (options: ConfirmOptions) => Promise<boolean>] {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOptions(null);
  };

  const element = options && (
    <Dialog
      width="narrow"
      title={options.title}
      onClose={() => settle(false)}
      footer={
        <>
          <Button variant="secondary" onClick={() => settle(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant={options.tone ?? 'danger'} onClick={() => settle(true)}>
            {options.confirmLabel}
          </Button>
        </>
      }
    >
      <p className="fs-dialog__consequence">{options.consequence}</p>
    </Dialog>
  );
  return [element, confirm];
}
